import {useCallback, useState} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Listbox} from '@headlessui/react';
import {useProductOptions} from '@shopify/hydrogen';

import {Text, IconCheck, IconCaret} from '~/components';
import {useImageOptimizer} from '~/hooks/useImageOptimizer';

/*
 * ProductOptions component
 * Displays a list of product options for a given product
 */
export function ProductOptions({values, ...props}) {
  // const asDropdown = values.length > 7;
  // NOTE: rendering options as grid for now - can consider if others may be better as a dropdown in the future
  const asDropdown = false;

  return asDropdown ? (
    <OptionsDropdown values={values} {...props} />
  ) : (
    <OptionsGrid values={values} {...props} />
  );
}

function OptionsGrid({values, name, handleChange}) {
  const {selectedOptions, variants: shopifyVariants} = useProductOptions();

  //reduce shopifyVariants to an object with keys of variant title and values of variant image url
  const shopifyVariantImageOptions = shopifyVariants.reduce((acc, variant) => {
    // dont use primary image for variants missing a unique image
    const isUniqueImage = !Object.values(acc).includes(variant.image?.url);
    acc[variant.title] =
      variant.image?.url && isUniqueImage ? variant.image.url : null;
    return acc;
  }, {});

  const draftVariants = shopifyVariants.filter((obj) => {
    return (
      obj.metafield &&
      obj.metafield.key === 'set_as_draft' &&
      obj.metafield.value === 'true'
    );
  });

  // filter out any variants that are "set as draft" (within shopify on variant metafield) so that they are not displayed on the front end
  const filteredValues = values.filter((value) => {
    return !draftVariants.some((variant) => variant.title === value);
  });

  return (
    <>
      {filteredValues.map((value) => {
        const checked = selectedOptions[name] === value;
        const id = `option-${name}-${value}`;

        return (
          <Text as="label" key={id} htmlFor={id}>
            <input
              type="radio"
              id={id}
              name={`option[${name}]`}
              value={value}
              checked={checked}
              onChange={() => handleChange(name, value)}
            />
            <div className={checked ? 'active' : ''}>
              <OptionImageOrText
                imageUrl={shopifyVariantImageOptions[value]}
                name={value}
              />
            </div>
          </Text>
        );
      })}
    </>
  );
}

/*
 * OptionImageOrText component
 * Displays an image if one is provided, otherwise displays the option name
 * Uses the useImageOptimizer hook to optimize the image URL
 */
function OptionImageOrText({imageUrl, name}) {
  const optimizedImage = useImageOptimizer(imageUrl, 100);

  return imageUrl ? (
    <img src={optimizedImage?.optimizedImageSrc} alt={name} loading="lazy" />
  ) : (
    name
  );
}

// (BOILERPLATE TO-DO): De-dupe UI with CountrySelector
function OptionsDropdown({values, name, handleChange}) {
  const [listboxOpen, setListboxOpen] = useState(false);
  const {selectedOptions} = useProductOptions();

  const updateSelectedOption = useCallback(
    (value) => {
      handleChange(name, value);
    },
    [name, handleChange],
  );

  return (
    <div className="relative w-full">
      <Listbox onChange={updateSelectedOption} value="">
        {/* @ts-expect-error @headlessui/react incompatibility with node16 resolution */}
        {({open}) => {
          setTimeout(() => setListboxOpen(open));
          return (
            <>
              <Listbox.Button
                className={`flex items-center justify-between w-full py-3 px-4 border border-primary ${
                  open ? 'rounded-b md:rounded-t md:rounded-b-none' : 'rounded'
                }`}
              >
                <span>{selectedOptions[name]}</span>
                <IconCaret direction={open ? 'up' : 'down'} />
              </Listbox.Button>

              <Listbox.Options
                className={`border-primary bg-contrast absolute bottom-12 z-30 grid
                h-48 w-full overflow-y-scroll rounded-t border px-2 py-2 transition-[max-height]
                duration-150 sm:bottom-auto md:rounded-b md:rounded-t-none md:border-t-0 md:border-b ${
                  listboxOpen ? 'max-h-48' : 'max-h-0'
                }`}
              >
                {values.map((value) => {
                  const isSelected = selectedOptions[name] === value;
                  const id = `option-${name}-${value}`;

                  return (
                    <Listbox.Option key={id} value={value}>
                      {/* @ts-expect-error @headlessui/react incompatibility with node16 resolution */}
                      {({active}) => (
                        <div
                          className={`text-primary w-full p-2 transition rounded flex justify-start items-center text-left cursor-pointer ${
                            active ? 'bg-primary/10' : null
                          }`}
                        >
                          {value}
                          {isSelected ? (
                            <span className="ml-2">
                              <IconCheck />
                            </span>
                          ) : null}
                        </div>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            </>
          );
        }}
      </Listbox>
    </div>
  );
}
